import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { hasPermission, hasRole } from "../utils/permissions";
import { HiMenuAlt2, HiOutlineBell, HiOutlineLogout } from "react-icons/hi";
import { MdDashboardCustomize, MdOutlineDateRange, MdOutlinePendingActions } from "react-icons/md";
import {
  FaCheckCircle,
  FaCog,
  FaDatabase,
  FaFileContract,
  FaFileSignature,
  FaHistory,
  FaLock,
  FaMoneyBillWave,
  FaUserCheck,
  FaUserPlus,
  FaUsers,
} from "react-icons/fa";
import { RiShieldCheckFill } from "react-icons/ri";
import { FcProcess } from "react-icons/fc";
import { IoIosArrowDown } from "react-icons/io";
import { TiCancelOutline } from "react-icons/ti";

const loanAllViewPermissions = ["loan_all_view_all", "loan_all_view_own", "loan_all_view_tagged", "loan_all_view_assigned"];
const loanViewPermissions = ["loan_view_all", "loan_view_own", "loan_view_tagged", "loan_view_assigned"];
const loanProcessingPermissions = [
  "loan_processing_view_all",
  "loan_processing_view_own",
  "loan_processing_view_tagged",
  "loan_processing_view_assigned",
  "loan_processing_verification",
  "loan_processing_mandate",
];

function Navbar() {
  const { logout, user } = useAuth();
  const location = useLocation();
  const navRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 6);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenMenu(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
  }, [location.pathname]);

  const menuItems = useMemo(() => ([
    { label: "Dashboard", icon: <MdDashboardCustomize size={16} />, path: "/" },
    {
      label: "Master Data",
      icon: <FaDatabase size={15} />,
      key: "master",
      subItems: [
        { label: "Users", icon: <FaUsers size={14} />, path: "/users", permissions: ["master_user_view"] },
        { label: "Roles", icon: <FaUserCheck size={14} />, path: "/roles", permissions: ["master_role_view"] },
        { label: "Schemes", icon: <RiShieldCheckFill size={14} />, path: "/scheme", permissions: ["master_scheme_view"] },
      ],
    },
    {
      label: "Customers",
      icon: <FaUsers size={15} />,
      key: "customers",
      subItems: [
        { label: "New Customer", icon: <FaUserPlus size={13} />, path: "/createcustomers", permissions: ["customer_create_module_create"] },
        { label: "Drafts", icon: <FaFileSignature size={13} />, path: "/draftcustomers", permissions: ["customer_draft_view_all", "customer_draft_view_own", "customer_draft_view_tagged", "customer_draft_view_assigned"] },
        { label: "Customer List", icon: <FaHistory size={13} />, path: "/customers", permissions: ["customer_list_view_all", "customer_list_view_own", "customer_list_view_tagged", "customer_list_view_assigned"] },
      ],
    },
    {
      label: "Loans",
      icon: <FaMoneyBillWave size={15} />,
      key: "loan",
      subItems: [
        { label: "All Loans", icon: <FaHistory size={13} />, path: "/view-loans", permissions: loanAllViewPermissions },
        { label: "Create Loan", icon: <FaFileSignature size={13} />, path: "/loan", permissions: ["loan_create"] },
        { label: "Draft Loans", icon: <MdOutlinePendingActions size={13} />, path: "/draft-loans", permissions: ["loan_draft_view_all", "loan_draft_view_own", "loan_draft_view_tagged", "loan_draft_view_assigned"] },
        { label: "Admin Approval", icon: <RiShieldCheckFill size={13} />, path: "/admin-approval", permissions: ["admin_approval_view"], roles: ["ADMIN"] },
        { label: "Processing", icon: <FcProcess size={13} />, path: "/loanverification", permissions: loanProcessingPermissions },
        { label: "Approved", icon: <FaCheckCircle size={13} />, path: "/viewloan", permissions: loanViewPermissions },
        { label: "Rejected Loans", icon: <TiCancelOutline size={13} />, path: "/rejected-loan", permissions: ["loan_rejected_view_all", "loan_rejected_view_own", "loan_rejected_view_tagged", "loan_rejected_view_assigned"] },
        {
          label: "Closure",
          icon: <FaLock size={13} />,
          key: "closedLoan",
          subItems: [
            { label: "NOC Pending", icon: <MdOutlinePendingActions size={12} />, path: "/noc-pending", permissions: ["loan_closed_view_all", "loan_closed_view_own", "loan_closed_view_tagged", "loan_closed_view_assigned"] },
            { label: "NOC Issued", icon: <FaCheckCircle size={12} />, path: "/noc-issue", permissions: ["loan_closed_view_all", "loan_closed_view_own", "loan_closed_view_tagged", "loan_closed_view_assigned"] },
          ],
        },
      ],
    },
    { label: "EMI Collection", icon: <FaMoneyBillWave size={15} />, path: "/emi-receiving", permissions: ["emi_view_all", "emi_view_own", "emi_view_tagged", "emi_view_assigned", "emi_create"] },
    {
      label: "Reports",
      icon: <FaFileContract size={15} />,
      key: "reports",
      subItems: [
        { label: "Loan Report", icon: <FaFileContract size={13} />, path: "/reports/loan", permissions: ["report_view"], roles: ["ADMIN"] },
        { label: "Collection Report", icon: <FaMoneyBillWave size={13} />, path: "/reports/collection", permissions: ["report_view"], roles: ["ADMIN"] },
        { label: "EMI Due Report", icon: <TiCancelOutline size={13} />, path: "/reports/due", permissions: ["report_view"], roles: ["ADMIN"] },
      ],
    },
    { label: "Settings", icon: <FaCog size={15} />, path: "/account-settings" },
  ]), []);

  const getVisibleMenuItem = (item) => {
    if (item.roles && !hasRole(user, item.roles)) return null;
    if (item.permissions && !hasPermission(user, item.permissions)) return null;
    if (!Array.isArray(item.subItems)) return item;
    const visibleSubItems = item.subItems.map(getVisibleMenuItem).filter(Boolean);
    if (!visibleSubItems.length) return null;
    return { ...item, subItems: visibleSubItems };
  };

  const visibleMenuItems = useMemo(
    () => menuItems.map(getVisibleMenuItem).filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [menuItems, user]
  );

  const currentDate = new Date().toLocaleDateString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const isActive = (item) => {
    if (item.path) return location.pathname === item.path;
    return item.subItems?.some((subItem) => isActive(subItem));
  };

  const renderSubItems = (items) => (
    <div className="py-1.5">
      {items.map((item) => {
        const nested = Array.isArray(item.subItems) && item.subItems.length > 0;
        if (nested) {
          return (
            <div key={item.label} className="px-2 py-1">
              <div className="flex items-center gap-2 px-2.5 py-1.5 text-[10px] font-extrabold uppercase tracking-wide text-[#555]">
                <span className="flex h-6 w-6 items-center justify-center rounded bg-[#e8eaf6] text-[#1a237e]">{item.icon}</span>
                {item.label}
              </div>
              <div className="ml-6 border-l border-[#dde] pl-2">
                {renderSubItems(item.subItems)}
              </div>
            </div>
          );
        }

        return (
          <Link
            key={item.path}
            to={item.path}
            className={`mx-1.5 flex items-center gap-2 rounded-md px-2.5 py-2 text-xs font-bold transition ${
              location.pathname === item.path ? "bg-[#e8eaf6] text-[#1a237e] ring-1 ring-[#c5cae9]" : "text-[#1a1a2e] hover:bg-[#f9f9ff] hover:text-[#1a237e]"
            }`}
          >
            <span className={`flex h-7 w-7 items-center justify-center rounded-md ${
              location.pathname === item.path ? "bg-white text-[#1a237e]" : "bg-[#e8eaf6] text-[#3949ab]"
            }`}>{item.icon}</span>
            <span className="flex-1">{item.label}</span>
          </Link>
        );
      })}
    </div>
  );

  return (
    <header
      ref={navRef}
      className={`fixed left-0 right-0 top-0 z-40 min-h-[64px] border-b border-slate-200/80 px-3 transition-all duration-300 lg:px-4 ${
        scrolled ? "bg-white/95 shadow-[0_8px_28px_rgba(26,35,126,0.08)] backdrop-blur-xl" : "bg-white/90 backdrop-blur-xl"
      }`}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-[#1a237e]" />
      <div className="flex min-h-[64px] items-center gap-2">
        <Link to="/" className="group flex shrink-0 items-center gap-2 rounded-md px-1 py-1 transition hover:bg-[#f9f9ff]">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-[#1a237e] p-0.5 shadow-sm ring-1 ring-[#dde]">
            <img src="/logo.jpeg" alt="Ritika Finance" className="h-full w-full rounded-md object-cover" />
          </span>
          <div className="hidden min-w-0 md:block">
            <h1 className="m-0 truncate text-sm font-black text-[#1a1a2e]">Ritika</h1>
          </div>
        </Link>

        <nav className="hidden min-w-0 flex-1 items-center justify-center gap-0.5 rounded border border-[#dde] bg-[#e8eaf6]/55 p-1 xl:flex">
          {visibleMenuItems.map((item) => {
            const active = isActive(item);
            const hasSubItems = Array.isArray(item.subItems) && item.subItems.length > 0;

            if (!hasSubItems) {
              return (
                <Link
                  key={item.label}
                  to={item.path}
                  className={`relative inline-flex h-8 items-center justify-center gap-1.5 rounded-md px-2 text-xs font-extrabold transition ${
                    active ? "bg-white text-[#1a237e] shadow-sm ring-1 ring-[#c5cae9]" : "text-[#555] hover:bg-white hover:text-[#1a237e] hover:shadow-sm"
                  }`}
                >
                  <span className={active ? "text-[#1a237e]" : "text-[#3949ab]"}>{item.icon}</span>
                  {item.label}
                  {active && <span className="absolute inset-x-2 bottom-0.5 h-0.5 rounded-full bg-[#1a237e]" />}
                </Link>
              );
            }

            return (
              <div key={item.label} className="relative">
                <button
                  type="button"
                  onClick={() => setOpenMenu((current) => current === item.key ? null : item.key)}
                  className={`relative inline-flex h-8 items-center justify-center gap-1.5 rounded-md px-2 text-xs font-extrabold transition ${
                    active || openMenu === item.key ? "bg-white text-[#1a237e] shadow-sm ring-1 ring-[#c5cae9]" : "text-[#555] hover:bg-white hover:text-[#1a237e] hover:shadow-sm"
                  }`}
                >
                  <span className={active || openMenu === item.key ? "text-[#1a237e]" : "text-[#3949ab]"}>{item.icon}</span>
                  {item.label}
                  <IoIosArrowDown className={`text-xs transition-transform ${openMenu === item.key ? "rotate-180" : ""}`} />
                  {active && <span className="absolute inset-x-2 bottom-0.5 h-0.5 rounded-full bg-[#1a237e]" />}
                </button>

                {openMenu === item.key && (
                  <div className="absolute left-0 top-10 w-64 overflow-hidden rounded border border-[#dde] bg-white shadow-[0_18px_45px_rgba(26,35,126,0.14)] ring-1 ring-black/5">
                    <div className="border-b border-[#dde] bg-[#e8eaf6] px-3 py-2">
                      <p className="m-0 text-xs font-black text-[#1a237e]">{item.label}</p>
                    </div>
                    {renderSubItems(item.subItems)}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-1.5 lg:gap-2">
          <button className="hidden h-8 items-center gap-1.5 rounded-md border border-[#dde] bg-white px-2 text-xs font-bold text-[#555] shadow-sm transition hover:border-[#c5cae9] hover:bg-[#f9f9ff] 2xl:flex">
            <span className="flex h-5 w-5 items-center justify-center rounded bg-[#e8eaf6]">
              <MdOutlineDateRange size={14} className="text-[#1a237e]" />
            </span>
            {currentDate}
          </button>

          <button className="hidden h-8 w-8 items-center justify-center rounded-md border border-[#dde] bg-white text-[#555] shadow-sm transition hover:border-[#c5cae9] hover:bg-[#f9f9ff] md:flex">
            <span className="relative flex h-5 w-5 items-center justify-center">
              <HiOutlineBell size={18} className="text-[#3949ab]" />
              <span className="absolute -right-1 -top-1 flex h-3 w-3 items-center justify-center rounded-full bg-red-500 text-[8px] text-white ring-2 ring-white">
                3
              </span>
            </span>
          </button>

          <Link to="/account-settings" className="hidden h-8 items-center gap-2 rounded-md border border-[#dde] bg-white px-2 shadow-sm transition hover:border-[#c5cae9] hover:bg-[#f9f9ff] lg:flex">
            <div className="text-right">
              <p className="m-0 max-w-[90px] truncate text-xs font-bold text-[#1a1a2e]">{user?.name || "Admin"}</p>
            </div>
            <div className="flex h-6 w-6 items-center justify-center overflow-hidden rounded bg-[#1a237e] text-xs font-extrabold text-white shadow-inner">
              {user?.name?.charAt(0)?.toUpperCase() || "A"}
            </div>
          </Link>

          <button
            type="button"
            onClick={logout}
            className="hidden h-8 w-8 items-center justify-center rounded-md border border-[#dde] bg-white text-[#555] shadow-sm transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 lg:inline-flex"
            title="Logout"
          >
            <HiOutlineLogout size={20} />
          </button>

          <button
            type="button"
            onClick={() => setMobileOpen((current) => !current)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[#dde] bg-white text-[#1a237e] shadow-sm transition hover:border-[#c5cae9] hover:bg-[#f9f9ff] xl:hidden"
            title="Menu"
          >
            <HiMenuAlt2 size={22} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="max-h-[calc(100vh-64px)] overflow-y-auto border-t border-[#dde] bg-white/95 pb-3 shadow-inner backdrop-blur-xl xl:hidden">
          <div className="flex items-center justify-between border-b border-[#dde] px-2 py-3">
            <div>
              <p className="m-0 text-[10px] font-extrabold uppercase tracking-[0.22em] text-[#555]">Navigation</p>
              <p className="m-0 text-sm font-black text-[#1a1a2e]">{user?.role_name || user?.role || "Workspace"}</p>
            </div>
            <Link to="/account-settings" className="flex h-9 w-9 items-center justify-center rounded-md bg-[#1a237e] text-sm font-extrabold text-white">
              {user?.name?.charAt(0)?.toUpperCase() || "A"}
            </Link>
          </div>
          {visibleMenuItems.map((item) => {
            const hasSubItems = Array.isArray(item.subItems) && item.subItems.length > 0;
            if (!hasSubItems) {
              return (
                <Link
                  key={item.label}
                  to={item.path}
                  className={`mx-1 flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-extrabold ${
                    location.pathname === item.path ? "bg-[#e8eaf6] text-[#1a237e]" : "text-[#1a1a2e] hover:bg-[#f9f9ff]"
                  }`}
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-[#e8eaf6] text-[#1a237e]">{item.icon}</span>
                  {item.label}
                </Link>
              );
            }

            return (
              <div key={item.label} className="border-t border-[#dde] py-2">
                <button
                  type="button"
                  onClick={() => setOpenMenu((current) => current === item.key ? null : item.key)}
                  className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-extrabold text-[#1a1a2e] hover:bg-[#f9f9ff]"
                >
                  <span className="flex items-center gap-3"><span className="flex h-8 w-8 items-center justify-center rounded-md bg-[#e8eaf6] text-[#1a237e]">{item.icon}</span>{item.label}</span>
                  <IoIosArrowDown className={`text-xs transition-transform ${openMenu === item.key ? "rotate-180" : ""}`} />
                </button>
                {openMenu === item.key && <div className="mx-1 rounded border border-[#dde] bg-[#f9f9ff]">{renderSubItems(item.subItems)}</div>}
              </div>
            );
          })}
          <button
            type="button"
            onClick={logout}
            className="mt-2 flex w-full items-center gap-2 rounded-md px-2 py-3 text-sm font-extrabold text-red-600 hover:bg-red-50"
          >
            <HiOutlineLogout size={18} /> Logout
          </button>
        </div>
      )}
    </header>
  );
}

export default Navbar;
